import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import useColors from 'hooks/useColors';
import { AppView, Button, ButtonGroup, Indicator, rem } from 'design-system';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation, } from 'utils/types/navigationV6';
import { SpaceUnavailableFlow, } from 'utils/types/navigation';
import Squircle from 'design-system/Squircle';
import { IconLogout } from 'design-system/icons';
import IconInfoTriangle from 'design-system/icons/IconInfoTriangle';
import SuccessView from 'design-system/SuccessView';
import { benefits, tierThemes } from 'features/premium/benefits';
import { muteErrorAndResult, useDisableAndroidBackButton, useFetchOld, useTimeout, } from 'hooks';
import AnimatedIconBell from 'features/pay/icons/AnimatedIconBell';
import { selectUserId } from 'reducers/selectors';
import Modal from 'utils/packages/Modal';
import { Tier } from 'features/premium/entities';
import iconSource from 'design-system/img/notifications.png';
import PaymentToastModal from 'features/pay/components/toast/PaymentToastModal';
import { nativeModalOptions } from 'hooks/navigation/useStackNavigatorScreenOptions';
import { navigationRef, popToTop } from 'utils/navigationv6';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import { selectDefaultSpaceId, selectSelectedSpaceInfo, selectSpaceInfoById, } from './reducers/selectors';
import { deleteSpaceMember, fetchSpacesAction, sendSpaceReminder, setSelectedSpace, setShowSpaceLock, } from './actions';
import { isWeb } from '../../constants';
import { useMySpaceAccounts } from './hooks/useMySpaceAccounts';
import { useSpaceInfo } from './hooks';
const SpaceUnavailableScreenContent = ({ navigation, spaceInfo: spaceInfoProp, }) => {
    const colors = useColors();
    const [spaceInfoFetched] = useSpaceInfo(spaceInfoProp.space.id, !spaceInfoProp.users);
    const spaceInfo = useMemo(() => spaceInfoProp.users ? spaceInfoProp : spaceInfoFetched || spaceInfoProp, [spaceInfoProp, spaceInfoFetched]);
    const { mySpaceAccounts } = useMySpaceAccounts();
    const userId = useAppSelector(selectUserId);
    const selectedSpaceInfo = useAppSelector(selectSelectedSpaceInfo);
    const defaultSpaceId = useAppSelector(selectDefaultSpaceId);
    const hasAccountsInSpace = useMemo(() => !!mySpaceAccounts.find(({ space }) => space.id === spaceInfo.space.id)
        ?.accounts.length, [mySpaceAccounts, spaceInfo.space.id]);
    const hasAccountsInAnyCustomSpace = useMemo(() => {
        for (let i = 0; i < mySpaceAccounts.length; i++) {
            if (mySpaceAccounts[i].space.createdBy === userId &&
                !mySpaceAccounts[i].space.isDefault &&
                mySpaceAccounts[i].accounts.length > 0)
                return true;
        }
        return false;
    }, [mySpaceAccounts, userId]);
    const flow = useMemo(() => {
        if (spaceInfo.space.createdBy === userId) {
            if (hasAccountsInAnyCustomSpace) {
                return SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS;
            }
            return SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_NO_ACCOUNTS;
        }
        if (selectedSpaceInfo?.space.isDefault ||
            selectedSpaceInfo?.space.isPremium) {
            if (hasAccountsInSpace) {
                return SpaceUnavailableFlow.IN_AVAILABLE_SPACE_WITH_ACCOUNTS;
            }
            return SpaceUnavailableFlow.IN_AVAILABLE_SPACE_NO_ACCOUNTS;
        }
        if (hasAccountsInSpace) {
            return SpaceUnavailableFlow.IN_EXPIRED_SPACE_WITH_ACCOUNTS;
        }
        return SpaceUnavailableFlow.IN_EXPIRED_SPACE_NO_ACCOUNTS;
    }, [
        hasAccountsInAnyCustomSpace,
        hasAccountsInSpace,
        selectedSpaceInfo?.space.isDefault,
        selectedSpaceInfo?.space.isPremium,
        spaceInfo.space.createdBy,
        userId,
    ]);
    const disableBack = flow === SpaceUnavailableFlow.IN_EXPIRED_SPACE_WITH_ACCOUNTS ||
        flow === SpaceUnavailableFlow.IN_EXPIRED_SPACE_NO_ACCOUNTS;
    useDisableAndroidBackButton(useCallback(() => {
        if (!disableBack)
            navigation.goBack();
        return true;
    }, [disableBack]));
    useLayoutEffect(() => {
        if (disableBack) {
            navigation.setOptions({
                gestureEnabled: false,
                headerBackVisible: false,
                ...nativeModalOptions,
            });
        }
    }, [colors.text.primary, disableBack]);
    const [, , isSendingReminder, fetchSendReminder] = useFetchOld(muteErrorAndResult);
    const [showToast, setShowToast] = useState(false);
    useTimeout(() => setShowToast(false), showToast ? 3000 : undefined);
    const onSendReminder = useCallback(async () => {
        const res = await fetchSendReminder(sendSpaceReminder(spaceInfo.space.id, 'SPACE_UPGRADE_REMINDER'));
        if (!res)
            return;
        setShowToast(true);
    }, [fetchSendReminder, spaceInfo.space.id]);
    const owner = useMemo(() => spaceInfo.users?.find(({ id }) => id === spaceInfo.space.createdBy), [spaceInfo.space.createdBy, spaceInfo.users]);
    const title = useMemo(() => getTitle(flow, spaceInfo.space.name), [flow, spaceInfo.space.name]);
    const subtitle = useMemo(() => getSubtitle(flow, spaceInfo.space.name, owner?.firstName), [flow, owner?.firstName, spaceInfo.space.name]);
    return (<AppView withFrame={false}>
      <PaymentToastModal isVisible={showToast} onCloseAction={() => setShowToast(false)} title="Sent a reminder to upgrade" message={`To ${owner?.firstName || 'owner'}`} iconSource={iconSource} style={styles.toast}/>
      <SuccessView disableFadeAnimation icon={<Squircle width={88} height={88} color={colors.elements.statusNegativeLight}>
            <IconInfoTriangle width={54} height={54} colorKey="statusNegative"/>
          </Squircle>} title={title} subTitle={subtitle}>
        <ButtonGroup type="floating">
          <SecondaryButton flow={flow} spaceInfo={spaceInfo} defaultSpaceId={defaultSpaceId} userId={userId} isSendingReminder={isSendingReminder} onSendReminder={onSendReminder}/>
          <PrimaryButton flow={flow} spaceInfo={spaceInfo} defaultSpaceId={defaultSpaceId} isSendingReminder={isSendingReminder} onSendReminder={onSendReminder}/>
        </ButtonGroup>
      </SuccessView>
    </AppView>);
};
const styles = StyleSheet.create({
    toast: {
        marginTop: rem(56),
    },
});
const Wrapper = (props) => {
    const { navigation, route } = props;
    const spaceInfo = useAppSelector((store) => selectSpaceInfoById(store, route.params.id));
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Space unavailable',
            headerTitle: '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    if (!spaceInfo) {
        return <Indicator dark/>;
    }
    return <SpaceUnavailableScreenContent {...props} spaceInfo={spaceInfo}/>;
};
export default Wrapper;
const getTitle = (flow, spaceName) => {
    switch (flow) {
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_NO_ACCOUNTS:
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS:
            return `${tierThemes[benefits.spaces.minTier].name} has expired`;
        default:
            return `The space "${spaceName}" is no longer available`;
    }
};
const getSubtitle = (flow, _spaceName, ownerName) => {
    const firstText = `The owner of the space${ownerName ? `, ${ownerName},` : ''} hasn’t renewed their ${tierThemes[benefits.spaces.minTier].name} subscription, so you are no longer able to use this space.`;
    switch (flow) {
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_NO_ACCOUNTS:
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS:
            return `Your ${tierThemes[benefits.spaces.minTier].name} subscription has expired. If you want to continue using custom Spaces, you need to upgrade your current plan.\n\nDo you want to upgrade?`;
        case SpaceUnavailableFlow.IN_EXPIRED_SPACE_WITH_ACCOUNTS:
        case SpaceUnavailableFlow.IN_EXPIRED_SPACE_NO_ACCOUNTS:
            return firstText;
        case SpaceUnavailableFlow.IN_AVAILABLE_SPACE_NO_ACCOUNTS:
            return `${firstText}\nYou can remind them to update their subscription or leave the Space if you no longer need access.`;
        case SpaceUnavailableFlow.IN_AVAILABLE_SPACE_WITH_ACCOUNTS:
            return `${firstText}\nYou can move the accounts to your Private Space to see them again, or leave them here until the owner updates their subscription.`;
        default:
            return '';
    }
};
const PrimaryButton = ({ flow, defaultSpaceId, spaceInfo, isSendingReminder, onSendReminder, }) => {
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const onContinue = useCallback(() => {
        if (!defaultSpaceId)
            return;
        if (flow === SpaceUnavailableFlow.IN_EXPIRED_SPACE_NO_ACCOUNTS) {
            Modal.showConfirmation({
                title: 'Switch to Private Space',
                subtitle: 'You no longer have access to this space, so we’re switching you to your Private Space',
                confirmButtonTitle: 'Okay',
                onConfirm: () => {
                    dispatch(setSelectedSpace(defaultSpaceId));
                    navigation.navigateDeprecated('Success', {
                        topTitle: 'App changed to:',
                        title: 'Private Space',
                        subTitle: 'Only assets that you have in this space will be visible in Feed.',
                        buttonText: 'Done',
                        onContinue: () => {
                            popToTop();
                            navigationRef.navigateDeprecated('Feed');
                            dispatch(setShowSpaceLock(false));
                            return true;
                        },
                    });
                },
                brandReversedConfirm: false,
            });
            return;
        }
        if (flow === SpaceUnavailableFlow.IN_EXPIRED_SPACE_WITH_ACCOUNTS) {
            navigation.navigateDeprecated('SpaceUnavailableMoveAccounts', {
                spaceInfo,
                flow,
            });
        }
    }, [defaultSpaceId, flow, spaceInfo]);
    const onUpgrade = useCallback(() => {
        if (isWeb && benefits.spaces.minTier !== Tier.free) {
            UpgradeWebModal.show(benefits.spaces.minTier);
        }
        else {
            navigation.navigateDeprecated('EmmaProModal', {
                benefitId: 'spaces',
                tier: Tier.ultimate,
                isTierRequired: true,
            });
        }
    }, []);
    switch (flow) {
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_NO_ACCOUNTS:
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS:
            return <Button brand title="Yes, upgrade plan" onPress={onUpgrade}/>;
        case SpaceUnavailableFlow.IN_AVAILABLE_SPACE_NO_ACCOUNTS:
        case SpaceUnavailableFlow.IN_AVAILABLE_SPACE_WITH_ACCOUNTS:
            return (<Button brand title="Remind owner to renew" onPress={onSendReminder} leftIcon={<AnimatedIconBell isFetching={isSendingReminder} tintColor={colors.text.white}/>}/>);
        case SpaceUnavailableFlow.IN_EXPIRED_SPACE_NO_ACCOUNTS:
        case SpaceUnavailableFlow.IN_EXPIRED_SPACE_WITH_ACCOUNTS:
            return <Button brand title="Continue" onPress={onContinue}/>;
        default:
            return null;
    }
};
const SecondaryButton = ({ flow, defaultSpaceId, spaceInfo, userId, isSendingReminder, onSendReminder, }) => {
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const [, , isLeaving, fetchLeave] = useFetchOld(muteErrorAndResult);
    const onLeaveSpace = useCallback(async () => {
        const res = await fetchLeave(deleteSpaceMember(spaceInfo.space.id, userId));
        if (!res)
            return;
        dispatch(fetchSpacesAction());
        navigation.navigateDeprecated('Success', {
            onContinue: () => {
                popToTop();
                navigationRef.navigateDeprecated('Feed');
                dispatch(setShowSpaceLock(false));
                return true;
            },
            buttonText: 'Done',
            title: `You have left the space "${spaceInfo.space.name}"`,
            subTitle: 'You no longer have access to this space',
            icon: (<Squircle width={88} height={88} color={colors.elements.statusOfflineLight}>
          <IconLogout width={54} height={54} colorKey="statusOffline"/>
        </Squircle>),
        });
    }, [
        colors.elements.statusOfflineLight,
        fetchLeave,
        spaceInfo.space.id,
        spaceInfo.space.name,
        userId,
    ]);
    const onRejectUpgrade = useCallback(() => {
        if (flow === SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS) {
            navigation.navigateDeprecated('SpaceUnavailableMoveAccounts', {
                spaceInfo,
                flow,
            });
            return;
        }
        if (flow === SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_NO_ACCOUNTS &&
            defaultSpaceId) {
            Modal.showConfirmation({
                title: 'Switch to Private Space',
                subtitle: 'You no longer have access to this space, so we’re switching you to your Private Space',
                confirmButtonTitle: 'Okay',
                onConfirm: () => {
                    dispatch(setSelectedSpace(defaultSpaceId));
                    navigation.navigateDeprecated('Success', {
                        topTitle: 'App changed to:',
                        title: 'Private Space',
                        subTitle: 'Only assets that you have in this space will be visible in Feed.',
                        buttonText: 'Done',
                        onContinue: () => {
                            popToTop();
                            navigationRef.navigateDeprecated('Feed');
                            dispatch(setShowSpaceLock(false));
                            return true;
                        },
                    });
                },
                brandReversedConfirm: false,
            });
        }
    }, [defaultSpaceId, flow, spaceInfo]);
    const onMoveAccounts = useCallback(() => {
        navigation.navigateDeprecated('SpaceUnavailableMoveAccounts', {
            spaceInfo,
            flow,
        });
    }, [flow, spaceInfo]);
    switch (flow) {
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_NO_ACCOUNTS:
        case SpaceUnavailableFlow.SPACE_OWNER_IN_EXPIRED_SPACE_WITH_ACCOUNTS:
            return (<Button brandReversed title="No, thanks" onPress={onRejectUpgrade}/>);
        case SpaceUnavailableFlow.IN_AVAILABLE_SPACE_NO_ACCOUNTS:
            return (<Button brandReversed title="Remove myself from this space" onPress={onLeaveSpace} isFetching={isLeaving}/>);
        case SpaceUnavailableFlow.IN_AVAILABLE_SPACE_WITH_ACCOUNTS:
            return (<Button brandReversed title="Move accounts" onPress={onMoveAccounts}/>);
        case SpaceUnavailableFlow.IN_EXPIRED_SPACE_NO_ACCOUNTS:
        case SpaceUnavailableFlow.IN_EXPIRED_SPACE_WITH_ACCOUNTS:
            return (<Button brandReversed title="Remind owner to renew" onPress={onSendReminder} leftIcon={<AnimatedIconBell isFetching={isSendingReminder} tintColor={colors.text.textOnBrand}/>}/>);
        default:
            return null;
    }
};
